import { useState, type FormEvent } from 'react';
import { Button, Card, CardHeader, Field, Icon, Modal } from './ui';
import { ApiError, api } from '../lib/api';

interface Result {
  name: string;
  formerRoot: string;
  recordsRewritten: number;
  youMovedTo: string;
}

/**
 * Putting a parent company above the top of the tree.
 *
 * The one operation that grows the hierarchy upward, and the only one that
 * moves the person performing it — scope looks downward only, so an
 * administrator who stayed put would be left with a parent company they
 * could not see. Offered solely to an administrator standing at the top.
 */
export function AddParentOrganisation({
  currentTop,
  onAdded,
}: {
  currentTop: string;
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [principalName, setPrincipalName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post<Result>('/org/parent', {
        name: name.trim(),
        ...(principalName.trim() ? { principalName: principalName.trim() } : {}),
      }));
      // Deliberately not refreshing yet: reloading the tree unmounts this card
      // while the data is in flight, which would take the confirmation with it.
      // The page catches up when the dialog is dismissed.
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader title="Parent organisation" />
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            <strong>{currentTop}</strong> is the top of your hierarchy. You can put a
            parent company above it — a holding group over one or more processors.
          </p>
          <div className="row">
            <Button onClick={() => setOpen(true)}>Add a parent above it</Button>
          </div>
        </div>
      </Card>

      <Modal
        open={open}
        title={result ? 'Done' : `Add a parent above ${currentTop}`}
        description={
          result
            ? undefined
            : 'Everything already in the tree keeps its level and its privileges. Only the paths change, and you move up to the new top.'
        }
        onClose={() => {
          setOpen(false);
          setName('');
          setPrincipalName('');
          setError(null);
          if (result) {
            setResult(null);
            onAdded();
          }
        }}
      >
        {result ? (
          <div className="stack">
            <div className="notice notice--ok" role="status">
              {Icon.check({ size: 15 })}
              <span>
                <strong>{result.name}</strong> now sits above {result.formerRoot}.{' '}
                {result.recordsRewritten} record paths were rewritten, and you are
                now at {result.youMovedTo}.
              </span>
            </div>
            <div className="row">
              <Button
                variant="primary"
                onClick={() => {
                  setOpen(false);
                  setResult(null);
                  onAdded();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form className="stack" onSubmit={submit}>
            {error && (
              <div className="notice notice--danger" role="alert">
                {Icon.alert({ size: 15 })}
                <span>{error}</span>
              </div>
            )}

            <div className="notice notice--info">
              {Icon.alert({ size: 15 })}
              <span>
                Every application, merchant, order and residual has its path
                rewritten. Nothing changes hands and nobody loses access — but it
                touches every record, so do it once and deliberately.
              </span>
            </div>

            <Field label="Name" htmlFor="parent-name">
              <input
                id="parent-name"
                className="input"
                required
                minLength={2}
                placeholder="Quantic"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>

            <Field label="Who runs it" hint="Optional." htmlFor="parent-principal">
              <input
                id="parent-principal"
                className="input"
                value={principalName}
                onChange={(e) => setPrincipalName(e.target.value)}
              />
            </Field>

            <div className="row">
              <Button type="submit" variant="primary" disabled={busy || name.trim().length < 2}>
                {busy ? 'Adding…' : `Put ${name.trim() || 'it'} above ${currentTop}`}
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
